import {
    useTable,
    useGlobalFilter,
    useSortBy,
    usePagination,
} from "react-table";
import {useEffect, useMemo} from "react";

import GlobalSearchFilter from "./TableSearch";
import {TableComponent} from "./TableComponent";
import PaginationNav from "./PaginationNav";

function Table(
    props: {
        tableData: any,
        getColumns: any,
        onRowClick?: any,
        pageIndex?: number,
        setPageIndex?: any,
        storageKey?: string,
        setRowdata?: any,
    }
) {
    const data = props.tableData;
    const columns = useMemo(() => props.getColumns, [props.getColumns]);

    // Storage key (allows multiple tables to persist independently)
    const storageKey = props.storageKey || "table";

    // Restore saved global filter from sessionStorage on first render (namespaced)
    const savedGlobalFilter = useMemo(() => {
        try {
            return sessionStorage.getItem(`${storageKey}:globalFilter`) || "";
        } catch {
            return "";
        }
    }, [storageKey]);

    // Persisted sorting: use a per-table storage key if provided
    const savedSortBy = useMemo(() => {
        try {
            const raw = sessionStorage.getItem(`${storageKey}:sortBy`);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }, [storageKey]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        state,
        setGlobalFilter,
        page: rows,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        state: {pageIndex},
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageSize: 25,
                globalFilter: savedGlobalFilter,
                sortBy: savedSortBy,
                pageIndex: props.pageIndex || 0
            },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    // Persist global filter whenever it changes (session-scoped, namespaced)
    useEffect(() => {
        try {
            sessionStorage.setItem(`${storageKey}:globalFilter`, state.globalFilter || "");
        } catch {
            // Ignore if storage is unavailable
        }
    }, [state.globalFilter, storageKey]);

    // Persist sortBy whenever it changes (session-scoped)
    useEffect(() => {
        try {
            sessionStorage.setItem(`${storageKey}:sortBy`, JSON.stringify(state.sortBy || []));
        } catch {
            // Ignore if storage is unavailable
        }
    }, [state.sortBy, storageKey]);

    // Track pageIndex changes
    useEffect(() => {
        props.setPageIndex && props.setPageIndex(pageIndex)
    }, [pageIndex, props, props.setPageIndex])

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row justify-between gap-2">
                <GlobalSearchFilter
                    className="sm:w-64"
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                />
            </div>
            <TableComponent
                getTableProps={getTableProps}
                headerGroups={headerGroups}
                getTableBodyProps={getTableBodyProps}
                rows={rows}
                prepareRow={prepareRow}
                onRowClick={props.onRowClick}
            />
            <div className="flex justify-center">
                <PaginationNav
                    gotoPage={gotoPage}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageCount={pageCount}
                    pageIndex={pageIndex}
                />
            </div>
        </div>
    );
}

export {Table};